import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const feedScrollDemoSkill: Skill = {
    name: SKILL_NAMES.feedScrollDemo,
    kind: 'codeo',
    description:
        'Scroll a mocked page or feed past a fixed viewport and stop on the thing that matters — ' +
        'for showing where a product appears in a stream, or walking down a page to one section.',
    instructions: `# Feed Scroll Demo

A column of content moves up behind a fixed viewport, decelerates, and settles with one item centred. The scroll establishes that there is more than the frame shows; the stop is what names the payload. Without the stop it is just a crawl.

Use it to place something in a context — a post in a feed, a section in a page, a row in a long table — where the surrounding material is the argument.

## Markup

The viewport clips; the track is taller than it and carries every item.

\`\`\`html
<div id="viewport" style="position: absolute; left: 640px; top: 160px; width: 640px; height: 760px; border-radius: 28px; overflow: hidden; background: var(--l5-color-1, #ffffff); box-shadow: 0 40px 90px rgba(0,0,0,0.28);">
  <div id="feed-track" style="position: absolute; left: 0; top: 0; width: 100%; display: flex; flex-direction: column; gap: 24px; padding: 24px;">
    <div class="feed-item" style="height: 300px; border-radius: 18px; background: var(--l5-color-0, #eef1f6);"></div>
    <div class="feed-item" style="height: 300px; border-radius: 18px; background: var(--l5-color-0, #eef1f6);"></div>
    <div id="feed-hero" class="feed-item" style="height: 340px; border-radius: 18px; padding: 32px; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 40px; font-weight: 800; color: var(--l5-color-1, #ffffff);">Your video, in the feed</div>
    <div class="feed-item" style="height: 300px; border-radius: 18px; background: var(--l5-color-0, #eef1f6);"></div>
  </div>
</div>
\`\`\`

## Timeline code

Measure where the hero has to land rather than guessing a scroll distance, so the stop is exact regardless of the items above it.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const viewportEl = document.querySelector('#viewport');
const heroEl = document.querySelector('#feed-hero');
const trackEl = document.querySelector('#feed-track');
// Scroll so the hero's centre meets the viewport's centre.
const settle = -(heroEl.offsetTop + heroEl.offsetHeight / 2 - viewportEl.clientHeight / 2);

gsap.set(trackEl, { y: 0 });

tl.to(trackEl, { y: settle, duration: 1.7, ease: 'power3.out' }, 0.5);
// The hero acknowledges the stop, so the scroll reads as having arrived somewhere.
tl.fromTo('#feed-hero', { scale: 0.97 }, { scale: 1, duration: 0.5, ease: 'back.out(1.6)' }, 1.9);

window.__timelines = { main: tl };
\`\`\`

\`power3.out\` is the shape of a flick that runs out of momentum. A linear scroll reads as a machine and a \`power2.inOut\` reads as a slider being dragged.

## Adapt

- **Overshoot and correct**: scrolling 40px past the settle point and easing back reads as a real thumb. Keep it small.
- **Horizontal rail**: the same technique on \`x\` with a row of cards — for a carousel of options where one is the answer.
- **Dim the surroundings**: fading the non-hero items to 0.5 opacity as the scroll settles makes the payload unmistakable without moving it.
- **Long page walk**: several stops chained, each with a short hold, when the point is the journey rather than one destination. Two stops maximum in a single scene.
- **With a cursor or thumb**: precede the scroll with a gesture indicator — see \`${SKILL_NAMES.simulatedCursorClick}\` for the register.
- **Placeholder items**: keep the non-hero items abstract. Real content in them competes with the payload for reading.

## Pitfalls

- The track is \`y\`-tweened, so it must not carry a CSS \`transform\`; lay it out with \`top: 0\` and let the tween own the offset.
- \`offsetTop\` is read once at build time, which is correct here because the content is static. Don't re-measure inside \`onUpdate\`.
- The viewport needs \`overflow: hidden\`, and nothing inside it should need \`data-l5-allow-offscreen\` — the clip is the point.
- It has to stop. A scroll that runs the whole scene is a crawl; use \`${SKILL_NAMES.tickerCrawlStrip}\` if that is what the content wants.
- Give the hero at least 1.5s of settled hold after the stop, or the viewer never reads what the scroll was for.
- Don't animate the items individually while the track is moving. One motion system at a time.
- No scrollbars, no browser chrome, no nav — the style guidance rules those out, and they would draw the eye away from the payload.`,
};
